/**
 * Deployment orchestrators for webenv.
 *
 * Two modes:
 * - `deployWebenv()` — from-scratch, deploys all 7 phases + seeds
 * - `deployWebenvFork()` — fork mode, discovers contracts + funds accounts
 */

import {
  deployContract,
  deployERC8004,
  deployUniswapStack,
  deployVaultCore,
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_BYTECODE,
  POOL_SWAP_TEST_ABI,
  POOL_SWAP_TEST_BYTECODE,
  seedAgents,
  seedDeposits,
  seedLiquidity,
  seedSwaps,
  seedTokens,
  seedVaults,
} from "@gotts.ai/devenv";

import { DEPLOYER } from "../runtime/accounts.js";
import { humanizeRpcError } from "../runtime/fork-config.js";
import {
  seedForkAccounts,
  seedForkV4Pools,
  TOKEN_BALANCE_SLOTS,
} from "../seed/fork-seed.js";

import { discoverContracts, getV2InitCodePairHash } from "./fork-discovery.js";
import {
  advanceProgress,
  buildDeployPhases,
  completeProgress,
  DEPLOY_PHASES,
  errorProgress,
  FORK_PHASES,
  initialProgress,
  phaseCompleted,
  phaseStarted,
  updateAction,
} from "./progress.js";

import type { TevmConfig } from "../runtime/types.js";
import type { DeploymentProgress, SeedSteps } from "./progress.js";
import type { CreatedVault, UniswapDeployment } from "@gotts.ai/devenv";
import type { Hex, PublicClient, TestClient, WalletClient } from "viem";

/** Clients needed for deployment. */
export interface DeployClients {
  publicClient: PublicClient;
  walletClient: WalletClient;
  testClient: TestClient;
}

/** Progress callback type. */
export type OnProgress = (progress: DeploymentProgress) => void;

/** Options for from-scratch deployment. */
export interface DeployWebenvOptions {
  clients: DeployClients;
  onProgress?: OnProgress;
  /** Which seed steps to run. Default: all. */
  seedSteps?: SeedSteps;
}

/**
 * Deploy the full Uniswap stack from scratch via Tevm.
 *
 * Runs all 7 deployment phases followed by 4 seed steps.
 * Reports progress via callback for UI overlay.
 */
export async function deployWebenv(
  options: DeployWebenvOptions,
): Promise<UniswapDeployment> {
  const { clients, onProgress, seedSteps } = options;

  // Build dynamic phase list based on which seed steps are enabled
  const activePhases = buildDeployPhases(seedSteps);

  const startMs = Date.now();
  let progress: DeploymentProgress = {
    ...initialProgress(false, activePhases.length),
    startedAt: startMs,
  };

  const report = (p: DeploymentProgress) => {
    progress = { ...p, elapsedMs: Date.now() - startMs };
    onProgress?.(progress);
  };

  try {
    // Deploy via devenv's deployer, using hooks for progress reporting
    const deployment = await deployUniswapStack({
      publicClient: clients.publicClient,
      walletClient: clients.walletClient,
      deployer: DEPLOYER.address,
      request: async (args) => clients.testClient.request(args as never),
      hooks: {
        beforeDeploy: () => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const phase = DEPLOY_PHASES[0]!;
          progress = phaseStarted(progress, phase.id);
          report(advanceProgress(progress, phase.id, phase.name, 0));
        },
        afterPhase: (result) => {
          // Record completed phase details
          const completedPhase = DEPLOY_PHASES[result.phase - 1];
          if (completedPhase) {
            progress = phaseCompleted(progress, completedPhase.id, {
              contractsDeployed: result.txHashes.length,
              gasUsed: result.gasUsed,
              txHashes: result.txHashes,
              durationMs: result.durationMs,
            });
          }

          // Advance to next phase
          const nextPhase = DEPLOY_PHASES[result.phase];
          if (nextPhase) {
            progress = phaseStarted(progress, nextPhase.id);
            report(
              advanceProgress(
                progress,
                nextPhase.id,
                nextPhase.name,
                nextPhase.index,
              ),
            );
          }
        },
        afterDeploy: () => {
          // Seed steps report their own progress via beforeSeedStep hook
        },
      },
      // Don't try to write to filesystem
      deploymentPath: "/dev/null",
    });

    // Seed test data — selective or full depending on seedSteps
    const ctx = {
      publicClient: clients.publicClient,
      walletClient: clients.walletClient,
      deployer: DEPLOYER.address,
      request: async (args: unknown) =>
        clients.testClient.request(args as never),
    };

    // Build a lookup from phase id to its index in the active phase list
    const phaseByIdMap = new Map(activePhases.map((p) => [p.id, p]));

    const reportSeedPhase = (phaseId: string) => {
      const phase = phaseByIdMap.get(phaseId as DeploymentProgress["phase"]);
      if (phase) {
        progress = phaseStarted(progress, phase.id);
        report(advanceProgress(progress, phase.id, phase.name, phase.index));
      }
    };

    const reportAction = (action: string) => {
      progress = updateAction(progress, action);
      report(progress);
    };

    // Determine which seeds to run (default: all)
    const steps = seedSteps ?? {
      tokens: true,
      liquidity: true,
      swaps: true,
      agents: true,
      vaults: true,
      deposits: true,
    };
    const runTokens = steps.tokens === true;
    const runLiquidity = steps.liquidity === true;
    const runSwaps = steps.swaps === true;
    const runAgents = steps.agents === true;
    const runVaults = steps.vaults === true;
    const runDeposits = steps.deposits === true;

    let tokens = deployment.tokens;

    if (runTokens) {
      reportSeedPhase("seed-tokens");
      const tokensResult = await seedTokens(
        ctx,
        deployment.foundation.weth9,
        reportAction,
      );
      tokens = tokensResult.tokens;
    }

    if (runLiquidity && tokens) {
      reportSeedPhase("seed-liquidity");
      await seedLiquidity(ctx, deployment, tokens, reportAction);
    }

    if (runSwaps && tokens) {
      reportSeedPhase("seed-swaps");
      await seedSwaps(ctx, deployment, tokens, undefined, reportAction);
    }

    if (runAgents) {
      reportSeedPhase("seed-agents");
      await seedAgents(ctx, deployment.erc8004, reportAction);
    }

    let createdVaults: CreatedVault[] = [];
    if (runVaults && tokens && deployment.vault) {
      reportSeedPhase("seed-vaults");
      const vaultsResult = await seedVaults(
        ctx,
        deployment.vault,
        tokens,
        reportAction,
      );
      createdVaults = vaultsResult.vaults;
    }

    if (runDeposits && createdVaults.length > 0 && deployment.vault) {
      reportSeedPhase("seed-deposits");
      await seedDeposits(ctx, deployment.vault, createdVaults, reportAction);
    }

    // Attach token addresses to deployment
    const fullDeployment: UniswapDeployment = {
      ...deployment,
      tokens: tokens ?? deployment.tokens,
    };

    report(completeProgress(progress, Date.now() - startMs));
    return fullDeployment;
  } catch (err) {
    const rawMsg = err instanceof Error ? err.message : String(err);
    report(
      errorProgress(progress, humanizeRpcError(rawMsg), Date.now() - startMs),
    );
    throw err;
  }
}

/** Options for fork deployment. */
export interface DeployWebenvForkOptions {
  clients: DeployClients;
  config: TevmConfig;
  onProgress?: OnProgress;
}

/**
 * Set up a forked environment.
 *
 * 1. Discover contracts at canonical addresses
 * 2. Fund 20 test accounts with ETH
 */
export async function deployWebenvFork(
  options: DeployWebenvForkOptions,
): Promise<UniswapDeployment> {
  const { clients, config, onProgress } = options;
  const startMs = Date.now();
  let progress: DeploymentProgress = {
    ...initialProgress(true),
    startedAt: startMs,
  };

  const report = (p: DeploymentProgress) => {
    progress = { ...p, elapsedMs: Date.now() - startMs };
    onProgress?.(progress);
  };

  try {
    // Phase 1: Discover contracts
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const discoverPhase = FORK_PHASES[0]!;
    progress = phaseStarted(progress, discoverPhase.id);
    report(
      advanceProgress(
        progress,
        discoverPhase.id,
        discoverPhase.name,
        discoverPhase.index,
      ),
    );

    const {
      addresses,
      found: _found,
      missing,
    } = await discoverContracts(clients.publicClient, config.chainId);

    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `Fork: ${String(missing.length)} contracts not found:`,
        missing.map((m) => m.name).join(", "),
      );
    }

    // Phase 2: Fund test accounts (ETH)
    // Accounts are already prefunded with ETH by the Tevm worker during init.
    // Skip the redundant setBalance loop to avoid hammering the fork RPC
    // (each setAccount call triggers a getAccount read from the remote).
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const fundPhase = FORK_PHASES[1]!;
    progress = phaseStarted(progress, fundPhase.id);
    report(
      advanceProgress(progress, fundPhase.id, fundPhase.name, fundPhase.index),
    );

    // Phase 3: Fund accounts with ERC-20 tokens
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const tokenPhase = FORK_PHASES[2]!;
    progress = phaseStarted(progress, tokenPhase.id);
    report(
      advanceProgress(
        progress,
        tokenPhase.id,
        tokenPhase.name,
        tokenPhase.index,
      ),
    );

    await seedForkAccounts(clients.testClient, config.chainId, (action) => {
      progress = updateAction(progress, action);
      report(progress);
    });

    // Build tokens mapping from TOKEN_BALANCE_SLOTS for the deployment manifest
    const prefix = config.chainId === 8453 ? "_BASE" : "_ETH";
    const slotMap: Record<string, `0x${string}`> = {};
    for (const [key, val] of Object.entries(TOKEN_BALANCE_SLOTS)) {
      if (key.endsWith(prefix)) {
        slotMap[key.replace(prefix, "").toLowerCase()] = val.address;
      }
    }
    const zeroAddr = "0x" as `0x${string}`;
    const tokens = {
      weth: slotMap.weth ?? zeroAddr,
      usdc: slotMap.usdc ?? zeroAddr,
      usdt: slotMap.usdt ?? zeroAddr,
      dai: slotMap.dai ?? zeroAddr,
      wbtc: slotMap.wbtc ?? zeroAddr,
    };

    // Jump the deployer nonce to a safe high value to avoid CREATE collisions.
    // The well-known Hardhat deployer (0xf39F...2266) has a public private key,
    // so many people have deployed contracts from it on mainnet. Any nonce that
    // was used for contract creation on mainnet will collide. Jump to nonce
    // 100000 which is well beyond any historical usage of this account.
    const nonceBefore = await clients.publicClient.getTransactionCount({
      address: DEPLOYER.address,
    });
    // eslint-disable-next-line no-console
    console.log(
      `[webenv:deploy] Deployer nonce BEFORE setNonce: ${nonceBefore}`,
    );

    await clients.testClient.request({
      method: "anvil_setNonce",
      params: [DEPLOYER.address, `0x${(100_000).toString(16)}`],
    } as never);

    const nonceAfter = await clients.publicClient.getTransactionCount({
      address: DEPLOYER.address,
    });
    // eslint-disable-next-line no-console
    console.log(
      `[webenv:deploy] Deployer nonce AFTER setNonce: ${nonceAfter} (expected 100000)`,
    );

    // Phase 4: Deploy V4 test helper contracts (PoolSwapTest, PoolModifyLiquidityTest)
    // These don't exist on mainnet — they're test contracts needed for direct V4 swaps/LP
    const v4PoolManager = addresses.v4PoolManager;
    let poolSwapTestAddr = "0x" as `0x${string}`;
    let poolModifyLiquidityTestAddr = "0x" as `0x${string}`;

    if (v4PoolManager) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const v4Phase = FORK_PHASES[3]!;
      progress = phaseStarted(progress, v4Phase.id);
      report(
        advanceProgress(progress, v4Phase.id, v4Phase.name, v4Phase.index),
      );

      const swapTest = await deployContract(
        clients.publicClient,
        clients.walletClient,
        DEPLOYER.address,
        {
          abi: POOL_SWAP_TEST_ABI,
          bytecode: POOL_SWAP_TEST_BYTECODE as Hex,
        },
        [v4PoolManager],
      );
      const modifyTest = await deployContract(
        clients.publicClient,
        clients.walletClient,
        DEPLOYER.address,
        {
          abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
          bytecode: POOL_MODIFY_LIQUIDITY_TEST_BYTECODE as Hex,
        },
        [v4PoolManager],
      );

      poolSwapTestAddr = swapTest.address;
      poolModifyLiquidityTestAddr = modifyTest.address;

      // Phase 4b: Seed V4 pools with liquidity
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const v4SeedPhase = FORK_PHASES[4]!;
      progress = phaseStarted(progress, v4SeedPhase.id);
      report(
        advanceProgress(
          progress,
          v4SeedPhase.id,
          v4SeedPhase.name,
          v4SeedPhase.index,
        ),
      );

      await seedForkV4Pools(
        clients.walletClient,
        clients.publicClient,
        DEPLOYER.address,
        v4PoolManager,
        poolModifyLiquidityTestAddr,
        tokens,
        (action) => {
          progress = updateAction(progress, action);
          report(progress);
        },
      );
    }

    // Phase 5: Deploy ERC-8004 Identity contracts
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const erc8004Phase = FORK_PHASES[5]!;
    progress = phaseStarted(progress, erc8004Phase.id);
    report(
      advanceProgress(
        progress,
        erc8004Phase.id,
        erc8004Phase.name,
        erc8004Phase.index,
      ),
    );

    const deployCtx = {
      publicClient: clients.publicClient,
      walletClient: clients.walletClient,
      deployer: DEPLOYER.address,
      request: async (args: unknown) =>
        clients.testClient.request(args as never),
    };

    const erc8004Result = await deployERC8004(deployCtx);

    // Phase 6: Deploy Vault Core contracts
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const vaultPhase = FORK_PHASES[6]!;
    progress = phaseStarted(progress, vaultPhase.id);
    report(
      advanceProgress(
        progress,
        vaultPhase.id,
        vaultPhase.name,
        vaultPhase.index,
      ),
    );

    const vaultResult = await deployVaultCore(
      deployCtx,
      {
        weth9: addresses.weth ?? ("0x" as `0x${string}`),
        permit2: addresses.permit2 ?? ("0x" as `0x${string}`),
      },
      erc8004Result.addresses,
    );

    // Build deployment manifest
    const block = await clients.publicClient.getBlock();
    const deployment: UniswapDeployment = {
      chainId: config.chainId,
      deployer: DEPLOYER.address,
      blockNumber: Number(block.number),
      timestamp: Number(block.timestamp),
      foundation: {
        weth9: addresses.weth ?? ("0x" as `0x${string}`),
        permit2: addresses.permit2 ?? ("0x" as `0x${string}`),
      },
      v2: {
        factory: addresses.v2Factory ?? ("0x" as `0x${string}`),
        router02: addresses.v2Router ?? ("0x" as `0x${string}`),
        initCodePairHash:
          getV2InitCodePairHash(config.chainId) ?? ("0x" as `0x${string}`),
      },
      v3: {
        factory: addresses.v3Factory ?? ("0x" as `0x${string}`),
        router: addresses.v3Router ?? ("0x" as `0x${string}`),
        quoterV2: addresses.v3QuoterV2 ?? ("0x" as `0x${string}`),
        nonfungiblePositionManager:
          addresses.v3NonfungiblePositionManager ?? ("0x" as `0x${string}`),
        nonfungibleTokenPositionDescriptor: "0x" as `0x${string}`,
        tokenDescriptor: "0x" as `0x${string}`,
        tickLens: "0x" as `0x${string}`,
        migrator: "0x" as `0x${string}`,
        staker: "0x" as `0x${string}`,
        swapRouter02: "0x" as `0x${string}`,
        owner: DEPLOYER.address,
      },
      v4: {
        poolManager: addresses.v4PoolManager ?? ("0x" as `0x${string}`),
        positionDescriptor: "0x" as `0x${string}`,
        positionManager: addresses.v4PositionManager ?? ("0x" as `0x${string}`),
        quoter: addresses.v4Quoter ?? ("0x" as `0x${string}`),
        stateView: addresses.v4StateView ?? ("0x" as `0x${string}`),
        poolModifyLiquidityTest: poolModifyLiquidityTestAddr,
        poolSwapTest: poolSwapTestAddr,
      },
      universalRouter: {
        router: addresses.universalRouter ?? ("0x" as `0x${string}`),
      },
      uniswapX: {
        exclusiveDutchOrderReactor:
          addresses.exclusiveDutchOrderReactor ?? ("0x" as `0x${string}`),
        priorityOrderReactor:
          addresses.priorityOrderReactor ?? ("0x" as `0x${string}`),
        v2DutchOrderReactor:
          addresses.v2DutchOrderReactor ?? ("0x" as `0x${string}`),
        orderQuoter: addresses.orderQuoter ?? ("0x" as `0x${string}`),
      },
      erc8004: erc8004Result.addresses,
      vault: vaultResult.addresses,
      tokens,
    };

    report(completeProgress(progress, Date.now() - startMs));
    return deployment;
  } catch (err) {
    const rawMsg = err instanceof Error ? err.message : String(err);
    report(
      errorProgress(progress, humanizeRpcError(rawMsg), Date.now() - startMs),
    );
    throw err;
  }
}

export type { UniswapDeployment } from "@gotts.ai/devenv";
